/**
 * scoreMatch — fzf-style fuzzy scoring.
 *
 * Inputs:
 *   haystack — the string being searched (e.g. "Notes › Categories")
 *   query    — the user's typed input
 *
 * Returns a MatchResult:
 *   { score, positions, matched }
 *
 *   score      — total weighted score, higher is better. 0 = no match.
 *   positions  — sorted indices into the ORIGINAL haystack of matched chars.
 *                Used by ResultHighlight to render <mark> ranges.
 *   matched    — number of chars covered; used by the row to fast-bail
 *                when the score is non-zero but the match is sparse.
 *
 * Algorithm (simplified fzf v2):
 *   1. Normalize both sides; tokenize query on whitespace + boundaries.
 *   2. For each query token, run a forward scan against the haystack:
 *        - reward word-start hits and consecutive runs heavily
 *        - acronym hits (one char per word) are first-class
 *        - exact substring of the haystack is a strong shortcut
 *      Every token must hit; if any token fails, score = 0.
 *   3. Aggregate per-token scores, subtract a small length penalty, and
 *      union the positions.
 *
 * Performance budget: scoring 5000 rows must take < 5 ms on a recent
 * laptop. The hot path uses charCodeAt + numeric arrays, no regex.
 */
import { normalize, tokenizeQuery, wordStarts } from "./tokenize";

export interface MatchResult {
  score: number;
  positions: number[];
  matched: number;
}

const NO_MATCH: MatchResult = { score: 0, positions: [], matched: 0 };

/** Bonuses (tunable; the test file freezes the expected ordering, not the
 *  raw numbers). */
const B_EXACT_TOKEN = 200;
const B_PREFIX_TOKEN = 150;
const B_ACRONYM_FULL = 140;
const B_SUBSTRING = 100;
const B_FIRST_CHAR = 20;
const B_WORD_START = 30;
const B_CONSECUTIVE = 16;
const P_GAP = 1;
const P_LENGTH = 1;

interface TokenMatch {
  score: number;
  positions: number[];
}

/** Best match for ONE token against ONE haystack. Returns NULL on miss. */
function matchToken(
  hay: string,
  hayStarts: boolean[],
  hayWordsByStart: number[],
  token: string,
): TokenMatch | null {
  if (token.length === 0) return { score: 0, positions: [] };

  // Shortcut 1: exact-token hit at a word boundary.
  let idx = hay.indexOf(token);
  while (idx >= 0) {
    const atWordStart = hayStarts[idx];
    const endsAtBoundary =
      idx + token.length === hay.length || hayStarts[idx + token.length];
    if (atWordStart && endsAtBoundary) {
      const positions: number[] = [];
      for (let k = 0; k < token.length; k++) positions.push(idx + k);
      return { score: B_EXACT_TOKEN - idx, positions };
    }
    idx = hay.indexOf(token, idx + 1);
  }

  // Shortcut 2: word-prefix hit (token is a prefix of a word inside haystack).
  for (const start of hayWordsByStart) {
    if (start + token.length > hay.length) continue;
    let ok = true;
    for (let k = 0; k < token.length; k++) {
      if (hay.charCodeAt(start + k) !== token.charCodeAt(k)) {
        ok = false;
        break;
      }
    }
    if (ok) {
      const positions: number[] = [];
      for (let k = 0; k < token.length; k++) positions.push(start + k);
      return { score: B_PREFIX_TOKEN - start, positions };
    }
  }

  // Shortcut 3: acronym hit — token's chars match the first char of N
  // consecutive words. Important for "tt" → "Task Templates".
  if (token.length >= 2 && token.length <= 8) {
    for (let s = 0; s < hayWordsByStart.length; s++) {
      let ok = true;
      const positions: number[] = [];
      for (let k = 0; k < token.length; k++) {
        const wIdx = hayWordsByStart[s + k];
        if (wIdx === undefined) {
          ok = false;
          break;
        }
        if (hay.charCodeAt(wIdx) !== token.charCodeAt(k)) {
          ok = false;
          break;
        }
        positions.push(wIdx);
      }
      if (ok) {
        return { score: B_ACRONYM_FULL - positions[0], positions };
      }
    }
  }

  // Shortcut 4: plain substring (no word-boundary requirement).
  idx = hay.indexOf(token);
  if (idx >= 0) {
    const positions: number[] = [];
    for (let k = 0; k < token.length; k++) positions.push(idx + k);
    let score = B_SUBSTRING - idx;
    if (hayStarts[idx]) score += B_WORD_START;
    return { score, positions };
  }

  // Fallback: subsequence with fzf-style bonuses.
  const positions: number[] = [];
  let score = 0;
  let last = -1;
  let consecutiveRun = 0;
  for (let qi = 0; qi < token.length; qi++) {
    const qc = token.charCodeAt(qi);
    let found = -1;
    for (let hi = last + 1; hi < hay.length; hi++) {
      if (hay.charCodeAt(hi) === qc) {
        found = hi;
        break;
      }
    }
    if (found < 0) return null;
    positions.push(found);
    if (found === 0) score += B_FIRST_CHAR;
    if (hayStarts[found]) score += B_WORD_START;
    if (last >= 0 && found === last + 1) {
      consecutiveRun += 1;
      score += B_CONSECUTIVE * consecutiveRun;
    } else {
      consecutiveRun = 0;
      if (last >= 0) score -= (found - last - 1) * P_GAP;
    }
    last = found;
  }
  // Slight discount for unfocused subsequence matches so they never
  // outweigh real substring matches.
  return { score: Math.max(1, score - 5), positions };
}

/** Public entry point. Cheap; safe to call thousands of times per keystroke. */
export function scoreMatch(haystack: string, query: string): MatchResult {
  if (!query) return { score: 1, positions: [], matched: 0 };
  if (!haystack) return NO_MATCH;
  const hay = normalize(haystack);
  const tokens = tokenizeQuery(query);
  if (tokens.length === 0) return { score: 1, positions: [], matched: 0 };

  const starts = wordStarts(hay);
  const wordsByStart: number[] = [];
  for (let i = 0; i < starts.length; i++) if (starts[i]) wordsByStart.push(i);

  // Track per-position best so we don't double-count overlapping token hits.
  const used = new Set<number>();
  let totalScore = 0;
  let totalMatched = 0;
  for (const token of tokens) {
    const m = matchToken(hay, starts, wordsByStart, token);
    if (!m) return NO_MATCH;
    totalScore += m.score;
    for (const p of m.positions) used.add(p);
    totalMatched += token.length;
  }
  const positions = [...used].sort((a, b) => a - b);
  // Length penalty — prefer denser, shorter matches over sprawling ones.
  totalScore -= Math.max(0, hay.length - totalMatched) * P_LENGTH;
  return {
    score: Math.max(0, totalScore),
    positions,
    matched: totalMatched,
  };
}

/** Score multiple haystacks with the same query and return the BEST. Each
 *  haystack carries a weight (0..1) so callers can mark a `title` as
 *  primary, `subtitle` as secondary, etc. */
export function scoreFields(
  fields: Array<{ text: string; weight: number; surfaceForHighlight?: boolean }>,
  query: string,
): {
  score: number;
  positions: number[];
  fieldIndex: number;
} {
  if (!query) return { score: 1, positions: [], fieldIndex: 0 };
  let best = -1;
  let bestField = 0;
  let bestPositions: number[] = [];
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i];
    if (!f.text) continue;
    const m = scoreMatch(f.text, query);
    if (m.score === 0) continue;
    const weighted = m.score * f.weight;
    if (weighted > best) {
      best = weighted;
      bestField = i;
      // Only surface positions for fields that consumers want to highlight
      // (e.g. don't highlight subtitles to avoid double-highlight noise).
      bestPositions = f.surfaceForHighlight === false ? [] : m.positions;
    }
  }
  return { score: Math.max(0, best), positions: bestPositions, fieldIndex: bestField };
}
